import { echo } from "@/echo";
import NotificationService from "@/services/NotificationService";
import { normalizeNotification } from "@/utils";
import { notification } from "ant-design-vue";

// store/notifications.js — registered under key "notification" but NOT
// namespaced: all actions/getters are dispatched with bare names.
//
// Scoped to the header bell + dropdown panel only: fetchNotifications always
// requests ?status=unread, so state.notifications is always "the current
// unread set" and state.unreadCount is the true bell badge count. The full
// notifications list page keeps its own local state (it needs read+unread+
// filtered views that don't belong in this shared slice) and just dispatches
// markAsRead/markAllAsRead here too, to keep the bell in sync.
export default {
  state: {
    notifications: [],
    unreadCount: 0,
    listening: false,
    listeningUserId: null,
    loading: false,
    // Bumped on every realtime push. Other components (the full notifications
    // list page) watch this to know when to refresh, without this module
    // needing to know they exist.
    pushSignal: 0,
  },
  mutations: {
    setNotifications(state, { notifications, count }) {
      state.notifications = notifications;
      state.unreadCount = typeof count === "number" ? count : notifications.length;
    },
    bumpPushSignal(state) {
      state.pushSignal++;
    },
    setNotificationsLoading(state, value) {
      state.loading = value;
    },
    markAsRead(state, id) {
      const existed = state.notifications.some((n) => n.id === id);
      if (existed) {
        state.notifications = state.notifications.filter((n) => n.id !== id);
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    markAllAsRead(state) {
      state.notifications = [];
      state.unreadCount = 0;
    },
    setListening(state, userId) {
      state.listening = !!userId;
      state.listeningUserId = userId || null;
    },
    resetNotifications(state) {
      state.notifications = [];
      state.unreadCount = 0;
    },
  },
  getters: {
    unreadNotifications: (state) => state.notifications,
    unreadCount: (state) => state.unreadCount,
    notificationsLoading: (state) => state.loading,
    notificationPushSignal: (state) => state.pushSignal,
  },
  actions: {
    async fetchNotifications({ commit }) {
      commit("setNotificationsLoading", true);
      try {
        const res = await NotificationService.fetch({ status: "unread" });
        const normalized = (res.notifications || []).map(normalizeNotification);
        // The API is paginated: "count" is just this page's item count,
        // "total" is the real grand total — that's what the bell badge
        // needs, not how many happened to come back in this page.
        commit("setNotifications", { notifications: normalized, count: res.total });
      } finally {
        commit("setNotificationsLoading", false);
      }
    },

    async markAsRead({ commit }, id) {
      await NotificationService.markAsRead(id);
      commit("markAsRead", id);
    },

    async markAllAsRead({ commit }) {
      await NotificationService.markAllAsRead();
      commit("markAllAsRead");
    },

    listenForNotifications({ commit, dispatch, state }, userId) {
      if (!userId || state.listeningUserId === userId) return;
      if (state.listeningUserId) {
        echo.leave(`notifications.${state.listeningUserId}`);
      }
      commit("setListening", userId);

      const channel = echo.private(`notifications.${userId}`);

      channel.subscribed(() => {
        console.log(`Subscribed to notifications.${userId}`);
      });
      channel.error((status) => {
        console.error(
          `Subscription error on notifications.${userId}:`,
          status
        );
      });

      // SystemNotification::broadcastAs() renames the event to
      // "system.notification", so Echo's .notification() helper won't fire.
      channel.listen(".system.notification", (data) => {
        // Toast is best-effort: never let a malformed payload throw out of the
        // socket handler, or the list refresh + pushSignal below get skipped.
        try {
          const normalized = normalizeNotification(data);
          // `content` can be HTML and arbitrarily long. normalizedDescription
          // is already the tag-stripped, single-line form — cap it so a big
          // payload can't blow the toast out of its 350px box.
          const body = String(normalized.normalizedDescription || "").trim();
          const MAX = 160;
          const description =
            body.length > MAX ? `${body.slice(0, MAX).trimEnd()}…` : body || undefined;

          notification.open({
            message: normalized.title || "New notification",
            description,
            placement: "topRight",
            duration: 4,
            style: {
              width: "350px",
              background: "#2ab57d",
            },
          });
        } catch (e) {
          console.error("Failed to render notification toast:", e);
        }
        // The broadcast payload's id doesn't match the DB row id, so
        // refetch the list instead of appending the payload directly —
        // keeps the bell list/count and mark-as-read ids consistent.
        dispatch("fetchNotifications");
        commit("bumpPushSignal");
      });
    },

    // Leaves the private channel and clears the previous account's
    // notifications so a different user logging in on the same tab
    // never sees stale data or receives another user's pushes.
    stopListening({ commit, state }) {
      if (state.listeningUserId) {
        echo.leave(`notifications.${state.listeningUserId}`);
      }
      commit("setListening", null);
      commit("resetNotifications");
    },
  },
};
